<template>
  <UtilGuard @complete="setup()" redirectToComplete class="space-y-4">
    <UtilMessage v-if="!auction.price && !isLoading" type="error">
      There was an problem loading this auction, please reload and try again.
    </UtilMessage>

    <template v-else>
      <div class="mb-2">
        <span class="font-medium">
          {{ !!auction.bidsCount ? 'Current' : 'Starting' }} price (per
          {{ listing.price && listing.price.unit === 'KG' ? 'kg' : 'head' }})
        </span>

        <Currency
          v-if="auction.price"
          :value="auction.price"
          class="block text-4xl font-extrabold tracking-none"
        ></Currency>

        <UtilTextLink
          v-if="!!auction.bidsCount"
          class="inline-block mt-2 font-medium underline text-brand"
          @click="isBidsModalOpen = true"
        >
          {{ $tc('listing.bids', auction.bidsCount) }} received
        </UtilTextLink>
      </div>

      <UtilListingOverviewDetails
        :details="details.auction"
        detail-class="text-xl font-extrabold text-text-secondary"
      >
        <template #countdown>
          <UtilListingCountdown :datetime="auction.endedAt">
            <template #default="{ days, hours, minutes, seconds }">
              <span v-if="days">{{ days }} days, {{ hours }} hrs</span>
              <span v-else-if="hours">{{ hours }} hrs, {{ minutes }} mins</span>
              <span
                v-else
                :class="['text-red-500', { 'animate-[blink_1s_ease-in-out_infinite]': !minutes && seconds < 30 }]"
              >
                {{ formatDoubleDigit(minutes) }}:{{ formatDoubleDigit(seconds) }}
              </span>
            </template>
          </UtilListingCountdown>
        </template>
      </UtilListingOverviewDetails>

      <template v-if="isApproved">
        <UtilInfo v-if="info" :icon="info.icon" :class="info.class" class="mb-2">
          <span class="font-bold">{{ info.title }}</span>
          {{ info.description }}
        </UtilInfo>

        <template v-if="auction.isOpen">
          <div class="grid grid-cols-1 gap-4 my-4">
            <UtilButton
              :icon="faHandPointUp"
              color="primary"
              class="text-gray-800 bg-legacy-action border-legacy-action"
              :class="{ 'opacity-50': auction.isUserWinning || listingIs.owner }"
              @click="onBidNowButtonClick()"
            >
              <!-- Bid {{ formatCurrency(auction.priceNext) }} -->
              Place Bid
            </UtilButton>
          </div>

          <div v-if="auction.isOpen" class="text-gray-darker !-mt-1 mb-4">
            <FontAwesomeIcon :icon="faInfoCircle" class="mr-1" />
            If a bid is placed in the final minute, 60 seconds are added to the closing time to allow more bidding.
          </div>

          <div class="">
            <div v-if="listingIs.premium" class="text-gray-darker">
              <FontAwesomeIcon :icon="faLock" class="mr-1" />
              All payments made are secure and reliable -
              <UtilTextLink to="/help/premium-listings#payment" class="text-brand">
                find out how we protect you.
              </UtilTextLink>
            </div>

            <UtilPaymentProtectionLink v-else :listing="listing" :listingIs="listingIs" />
          </div>
        </template>

        <template
          v-else-if="
            !auction.isOpen &&
            !listingIs.premium &&
            ((listingIs.owner && auction.bidsCount > 0) || auction.isUserWinning)
          "
        >
          <h3 class="mt-4 mb-2 text-lg font-medium">Next steps</h3>

          <UtilButton
            v-if="listingIs.owner && auction.bidsCount > 0"
            color="primary"
            @click="contactWinningBidder()"
            class="flex items-center justify-center w-full space-x-1"
          >
            <FontAwesomeIcon :icon="faCommentAltLines" />
            <span>Message winning bidder</span>
          </UtilButton>

          <UtilButton
            v-if="auction.isUserWinning"
            color="primary"
            @click="contactSeller()"
            class="flex items-center justify-center w-full space-x-1"
          >
            <FontAwesomeIcon :icon="faCommentAltLines" />
            <span>Message seller</span>
          </UtilButton>
        </template>
      </template>

      <template v-else>
        <UtilMessage v-if="isApprovalPending" type="information" class="mb-2">
          Approval under review
          <p>
            We have notified one of the team who will review you request ASAP. This message will not show once you
            have been approved.
          </p>
        </UtilMessage>

        <UtilButton
          v-else
          :icon="faUserCheck"
          :block="true"
          color="primary"
          class="mb-2 text-gray-800 bg-legacy-action border-legacy-action"
          @click="onApprovalButtonClick()"
        >
          Get approved for bidding
        </UtilButton>
      </template>
    </template>

    <ModalAuctionBid
      v-if="isAuctionModalOpen"
      @close="onBidModalClose"
      @fetchBids="getBids"
      :modalData="{
        auction: this.auction,
        listing: this.listing,
        bid: this.bid
      }"
    />

    <ModalAuctionBids
      v-if="isBidsModalOpen"
      @close="onBidsModalClose"
      :modalData="{
        auction,
        details,
        bids
      }"
    />

    <!-- <pre>{{ auction }}</pre> -->
  </UtilGuard>
</template>

<script>
import UtilButton from '@/components/utils/UtilButton'
import UtilTextLink from '@/components/utils/UtilTextLink'
import UtilInfo from '@/components/utils/listing/UtilInfo'
import UtilListingCountdown from '@/components/utils/listing/UtilListingCountdown'
import UtilListingOverviewDetails from '@/components/utils/listing/UtilListingOverviewDetails'
import UtilGuard from '@/components/utils/UtilGuard'
import BridgeApi from '@/lib/api/bridge'
import UtilMessage from '@/components/utils/UtilMessage'
import UtilPaymentProtectionLink from '@/components/utils/UtilPaymentProtectionLink'
import ModalAuctionBid from '@/components/modals/ModalAuctionBid.vue'
import ModalAuctionBids from '@/components/modals/ModalAuctionBids.vue'
import { formatCurrency, formatDoubleDigit } from '@/lib/helpers/transform'
import {
  faQuestionCircle,
  faTrophy,
  faEye,
  faSirenOn,
  faHourglass,
  faUserCheck,
  faInfoCircle,
  faLock,
  faCommentAltLines
} from '@fortawesome/pro-regular-svg-icons'
import { faHandPointUp, faFrownOpen } from '@fortawesome/pro-solid-svg-icons'

export default {
  name: 'UtilListingOverviewAuction',
  components: {
    UtilListingOverviewDetails,
    UtilButton,
    UtilTextLink,
    UtilInfo,
    UtilListingCountdown,
    UtilMessage,
    UtilGuard,
    UtilPaymentProtectionLink,
    ModalAuctionBid,
    ModalAuctionBids
  },
  props: {
    listing: { type: Object },
    listingIs: { type: Object }
  },
  data() {
    return {
      bid: {},
      message: {},
      helpers: {},
      bidApi: new BridgeApi(),
      bidsApi: new BridgeApi(),
      bidDeleteApi: new BridgeApi(),
      messageApi: new BridgeApi(),
      isAuctionModalOpen: false,
      isBidsModalOpen: false,
      isWaitingApproval: false,
      viewers: Math.floor(Math.random() * (15 - 5 + 1)) + 5,
      formatCurrency,
      formatDoubleDigit,
      faQuestionCircle,
      faUserCheck,
      faInfoCircle,
      faHandPointUp,
      faLock,
      faCommentAltLines
    }
  },
  computed: {
    bids() {
      return this.bidsApi.data?.bids
    },
    auction() {
      return {
        ...this.bidsApi.data,
        bids: undefined
      }
    },
    info() {
      const auction = this.auction
      if (!auction.isOpen && auction.isUserWinning && !auction.isReserveMet) {
        return {
          icon: faHourglass,
          title: 'Well done!',
          description: `You are currently the highest bidder, but the reserve price was not met. ${
            this.listingIs.premium
              ? ' We will contact the owner and follow up with you in the next working day.'
              : 'Message the seller to see if they will accept your bid.'
          }`,
          class: 'bg-warning-lighter border-warning'
        }
      } else if (!auction.isOpen && auction.isUserWinning) {
        return {
          icon: faTrophy,
          title: 'Congratulations!',
          description: `You are the highest bidder. ${
            this.listingIs.premium
              ? 'We will be in touch in the next working day with the next steps.'
              : 'Message the seller to arrange next steps.'
          }`,
          class: 'bg-success-lighter border-success text-primary'
        }
      } else if (!auction.isOpen && auction.isUserBidding && !auction.isUserWinning) {
        return {
          icon: faFrownOpen,
          title: 'Not this time!',
          description: `Unfortunately, you were not successful this time, but we greatly appreciate your participation.`,
          class: 'bg-danger-lighter border-danger text-primary'
        }
      } else if (auction.isUserBidding && auction.isUserWinning) {
        return {
          icon: faTrophy,
          title: `You're winning!`,
          description: 'You are currently the highest bidder.',
          class: 'bg-success-lighter border-success text-primary'
        }
      } else if (auction.isUserBidding && !auction.isUserWinning) {
        return {
          icon: faSirenOn,
          title: `You've been outbid!`,
          description: 'Place another bid to stay in the running.',
          class: 'bg-danger-lighter border-danger text-primary'
        }
      } else {
        return {
          icon: faEye,
          title: `${this.viewers} buyer(s)`,
          description: `are currently viewing this listing.`,
          class: 'bg-information-lighter border-information text-primary'
        }
      }
    },
    details() {
      return {
        auction: [
          {
            label: 'Status',
            detail: this.auction.isOpen ? 'Live' : 'Closed',
            textClass: this.auction.isOpen ? 'text-green-500' : 'text-red-600'
          },
          {
            label: 'Reserve',
            detail: this.auction.isReserveMet ? 'Met' : 'Not met',
            textClass: this.auction.isReserveMet ? '!text-green-500' : 'text-amber-500'
          },
          {
            key: 'countdown',
            label: 'Ending',
            class: 'min-w-16',
            disabled: !this.auction.isOpen
          },
          {
            label: 'Your last bid',
            detail: formatCurrency(this.auction.userPrice),
            textClass: this.auction.isUserWinning ? '!text-green-500' : '',
            disabled: this.listingIs.owner || !!this.auction.userMaxBidPrice || this.auction.userBidsCount === 0
          },
          {
            label: 'Your max bid',
            detail: formatCurrency(this.auction.userMaxBidPrice),
            disabled: this.listingIs.owner || !this.auction.userMaxBidPrice
          }
        ].filter(item => item.disabled !== true),
        bidding: [
          {
            label: 'Current bid',
            detail: formatCurrency(this.auction.price)
          },
          { label: 'Bids', detail: this.auction.bidsCount },
          { label: 'Bidders', detail: this.auction.bidsUserCount }
        ].filter(item => item.disabled !== true)
      }
    },
    confirm() {
      return [
        {
          label: 'Your bid',
          value: formatCurrency(this.bid.price),
          disabled: !!this.bid.max
        },
        {
          label: 'Your next bid',
          value: formatCurrency(this.bid.price),
          disabled: !this.bid.max
        },
        {
          label: 'Your max bid',
          value: formatCurrency(this.bid.max),
          disabled: !this.bid.max
        }
      ].filter(item => item.disabled !== true)
    },
    isLoading() {
      return this.bidsApi.loading && !this.bidsApi.attempt
    },
    isApproved() {
      return this.appIs.auth && this.$store.getters['app/isPermissed']('buying')
    },
    isApprovalPending() {
      return window?.localStorage['sml-approval-pending'] || this.isWaitingApproval
    }
  },
  methods: {
    async getBids() {
      if (this.appIs.auth) {
        await this.bidsApi.get(`/v1/listings/${this.listing.listingRef}/bids?userRef=${this.app.user.userRef}`)

        this.bid.price = this.auction.priceNext
      } else {
        this.bidsApi.loading = false
      }
    },

    onBidModalClose() {
      this.isAuctionModalOpen = false
      this.getBids()

      this.$scroll.intoViewIfNeeded(document.getElementById('auction-overview'))
    },

    onBidsModalClose() {
      this.isBidsModalOpen = false
      this.getBids()

      this.$scroll.intoViewIfNeeded(document.getElementById('auction-overview'))
    },

    async postMessage() {
      const { listing } = this
      await this.messageApi
        .body({
          toUserRef: this.env('SUPPORT_USER_REF'),
          subject: this.formatListingTitle(listing),
          ...this.message
        })
        .post('/api/legacy/messages')
      if (this.messageApi.is('success')) {
        this.$store.closeModal()
        this.message.message = ''
        this.$store.dispatch('auction/log', {
          type: 'message',
          data: {
            ...this.messageApi.response
          }
        })
        alert(`Your message has been sent. We attempt to reply within the next working day.`)
      } else {
        alert(this.messageApi.response?.message)
      }
    },
    getRefresh(options = {}) {
      if (document.visibilityState === 'visible') {
        if (this.isRefresh() || options.immediate) {
          this.getBids()
        }
      }
    },
    isRefresh() {
      if (this.listingIs.live === true) {
        const seconds = new Date().getSeconds()
        if (seconds % this.auction.interval === 0) {
          return true
        } else {
          return false
        }
      }
    },
    onDocumentVisibilityChange() {
      this.getRefresh({ immediate: true })
    },
    async onApprovalButtonClick() {
      this.isWaitingApproval = true
      window.localStorage['sml-approval-pending'] = true

      this.$store.dispatch('auction/log', { type: 'bid:approval' })
    },
    async onMessageButtonClick() {
      await this.postMessage()
    },
    onBidNowButtonClick() {
      if (this.auction.isUserWinning) {
        alert('You do not need to bid as you are currently winning.')
        this.$store.dispatch('auction/log', { type: 'bid:winner' })
        return
      }

      if (this.listingIs.owner) {
        alert('You cannot bid on your own listing.')
        this.$store.dispatch('auction/log', { type: 'bid:owner' })
        return
      }

      this.bid.price = this.auction.priceNext

      this.isAuctionModalOpen = true

      this.$store.dispatch('auction/log', {
        type: 'bid:button'
      })
    },
    onBidSuccessButtonClick() {
      this.$store.closeModal()
    },
    setup() {
      this.getBids()
      this.helpers.interval = window.setInterval(this.getRefresh, 1000)
      document.addEventListener('visibilitychange', this.onDocumentVisibilityChange)
      window.addEventListener('focus', this.onDocumentVisibilityChange)
    },

    contactWinningBidder() {
      this.$modal.open('ModalListingContactUser', {
        listing: this.listing,
        listingIs: this.listingIs,
        title: this.listing.title,
        otherUser: this.bids[0].user
      })
    },

    contactSeller() {
      this.$modal.open('ModalListingContactUser', {
        listing: this.listing,
        listingIs: this.listingIs,
        title: this.listing.title,
        otherUser: this.listing.user[0]
      })
    }
  },
  mounted() {
    this.setup()
  },
  beforeDestroy() {
    window.clearInterval(this.helpers.interval)
    document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange)
    window.removeEventListener('focus', this.onDocumentVisibilityChange)
  }
}
</script>
